import { useMemo, useState } from 'react';

import { Button, Card, Form, FormProps, Table, DatePicker } from 'antd';
import dayjs from 'dayjs';

import { getColumnEInvoiceStatus } from 'constants/table';

import 'styles/einvoice-status.scss';
import { useTranslation } from 'react-i18next';
import { useGetEInvoiceStatus } from 'hooks/useGetEInvoiceStatus';

function EInvoiceStatus() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [date, setDate] = useState<string | undefined>(dayjs().format('YYYY-MM-DD'));
  const { data, isFetching } = useGetEInvoiceStatus(date);

  const dataSource = useMemo(() => {
    if (!data) return [];

    const mappedData = data.flatMap(item =>
      item.sale_order_info.map((so, soIndex) => ({
        booking_id: item.booking_id,
        sale_order_name: so.sale_order_name,
        guest_name: item.guest_name,
        travel_agency_name: item.travel_agency_name,
        check_in_date: item.check_in_date,
        check_out_date: item.check_out_date,
        minvoice_signed: so.minvoice_signed,
        requested: so.einvoice_option === 'requested' ? 1 : 0,
        unrequested: so.einvoice_option === 'unrequested' ? 1 : 0,
        rowSpan: soIndex === 0 ? item.sale_order_info.length : 0
      }))
    );

    const totalSO = data?.reduce((acc, item) => acc + item.sale_order_info.length, 0);
    const totalUnrequested = data?.reduce(
      (acc, item) =>
        acc + item.sale_order_info.filter(so => so.einvoice_option === 'unrequested').length,
      0
    );
    const totalEInvoice = data?.reduce(
      (acc, item) =>
        acc + item.sale_order_info.filter(so => so.einvoice_option === 'requested').length,
      0
    );

    if (totalSO > 0 || totalUnrequested > 0 || totalEInvoice > 0) {
      const totalRow = {
        booking_id: t('common.totalCap'),
        sale_order_name: totalSO,
        guest_name: '',
        travel_agency_name: '',
        check_in_date: '',
        check_out_date: '',
        unrequested: totalUnrequested || 0,
        requested: totalEInvoice || 0,
        rowSpan: 1,
        isTotalRow: true
      };

      return [...mappedData, totalRow];
    }

    return mappedData;
  }, [data, t]);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});
    setDate(truthyValues.date.format('YYYY-MM-DD') || undefined);
  };

  const columns = getColumnEInvoiceStatus
    ? getColumnEInvoiceStatus(t).map(column => {
        const originalRender = column.render;

        return {
          ...column,
          render: (text: any, record: any, index: any) => {
            if (record.isTotalRow) {
              return <strong>{text}</strong>;
            }

            if (originalRender) {
              return originalRender(text, record, index);
            }
            return text;
          }
        };
      })
    : [];

  return (
    <div className="pms-einvoice-status">
      <Card title={t('eInvoiceStatus.title')} style={{ width: '100%' }}>
        <Form
          form={form}
          name="einvoice-status-filter-form"
          layout="inline"
          initialValues={{
            date: dayjs()
          }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <div className="pms-einvoice-status__filter-wrapper">
            <div className="pms-einvoice-status__filter">
              <Form.Item name="date" className="m-0">
                <DatePicker format={'DD-MM-YYYY'} placeholder={t('common.time.selectDate')} />
              </Form.Item>

              <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                {t('common.actions.submitButton')}
              </Button>
            </div>
          </div>
        </Form>

        <div className="pms-einvoice-status__table">
          <Table
            size="small"
            loading={isFetching}
            rowKey={record => `${record.booking_id}-${record.sale_order_name}`}
            columns={columns}
            dataSource={dataSource}
            bordered={true}
            pagination={false}
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            scroll={{ x: 500, y: 'calc(100vh - 320px)' }}
          />
        </div>
      </Card>
    </div>
  );
}

export default EInvoiceStatus;
